import { useState } from 'react'
import PropTypes from 'prop-types'
import { CheckCircle, Info, WarningCircle, XCircle } from '@phosphor-icons/react'

import { Tooltip } from '@/components/ui/tooltip'
import { formatDateOnly } from '@/utils/date/format-date-only'

import ConfirmPopup from '../../../Popups/ConfirmPopup'

// Copy is specified verbatim in the tickets. Mirrored from
// src/pages/clients/client-profile/keyless-access/igloo/igloo-pin-block.tsx (EL-12914 — keep in
// sync until the two trees are hoisted to a shared module).
const PIN_TOOLTIP =
  'Issue an Igloo PIN as a backup access method for this resident. Residents should primarily ' +
  'use BLE keyless access in the ElevateOS app. PIN access could be issued upon request. The PIN ' +
  'will be sent to the resident by email. Revoking the PIN in ElevateOS removes the PIN record ' +
  'from ElevateOS, but disabling it on the physical lock may require Bluetooth sync and lock ' +
  'reset based on Igloo behavior.'

const SYNC_TOOLTIP =
  'Upon completion of the revocation process, staff are required to physically approach the ' +
  'resident\'s lock and utilize the "Revoke PIN" feature within the Elevated Hub mobile ' +
  'application to ensure the PIN code is fully removed from the device.'

// Note: mirrors src/styles/base/customizations-elevatedliving.scss ($ctp-color__*). Not imported
// from src/helpers/constants.js — that module reads import.meta.env at load time, which breaks
// under Jest's CommonJS transform.
const ICON_COLORS = {
  primary: '#000000',
  red: '#ce2c21',
  green: '#548671',
  grey: '#646464',
}

const pinBlockStyle = {
  borderTop: '1px solid #ebebeb',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  marginTop: '2vh',
  paddingTop: '2vh',
  width: '100%',
}

const rowStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  justifyContent: 'space-between',
}

const infoGroupStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
}

const warningStyle = {
  alignItems: 'center',
  backgroundColor: '#fbf0f0',
  border: '1px solid #f5d4d2',
  borderRadius: '5px',
  display: 'flex',
  gap: '6px',
  padding: '8px 12px',
}

const IglooPinBlock = ({
  pin,
  canEdit,
  residentName,
  onIssue,
  onRevoke,
  onResend,
  isIssuing,
  isRevoking,
  isResending,
}) => {
  const [showIssueConfirm, setShowIssueConfirm] = useState(false)
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)

  if (pin.status === 'none') {
    if (!canEdit) return null
    return (
      <div style={pinBlockStyle}>
        <div style={infoGroupStyle}>
          <button
            type="button"
            className={`button button-primary ${isIssuing ? 'is-loading' : ''}`}
            disabled={isIssuing}
            onClick={() => setShowIssueConfirm(true)}
          >
            Issue Unit PIN Code Access
          </button>
          <Tooltip triggerIcon={Info} placement="bottom-end">
            {PIN_TOOLTIP}
          </Tooltip>
        </div>
        <ConfirmPopup
          title="Issue Unit PIN Code Access"
          body="This will issue a Unit PIN code for this resident and email it to them. Do you wish to proceed?"
          cancelText="Close"
          confirmText="Yes, issue"
          isOpen={showIssueConfirm}
          isLoading={isIssuing}
          cancelCallback={() => setShowIssueConfirm(false)}
          confirmCallback={async () => {
            try {
              await onIssue()
            } finally {
              setShowIssueConfirm(false)
            }
          }}
          className="is-caution"
        />
      </div>
    )
  }

  if (pin.status === 'revoked' || pin.status === 'revoked_pending_sync') {
    // Only show a date sourced from an actual revocation timestamp — never fall back to
    // issuedAt (an explicit backend status:'revoked' can arrive without revokedAt).
    const revokedOn = pin.revokedAt
    return (
      <div style={pinBlockStyle}>
        <div style={infoGroupStyle}>
          <span>PIN Revoked{revokedOn ? ` ${formatDateOnly(revokedOn)}` : ''}</span>
          <CheckCircle size={18} color={ICON_COLORS.green} />
          {pin.status === 'revoked_pending_sync' && (
            <>
              <span style={{ color: ICON_COLORS.grey }}>|</span>
              <span>Unit lock sync: Required</span>
              <Tooltip triggerIcon={Info} placement="bottom-end">
                {SYNC_TOOLTIP}
              </Tooltip>
            </>
          )}
        </div>
      </div>
    )
  }

  // status === 'active'
  return (
    <div style={pinBlockStyle}>
      <div style={rowStyle}>
        <div style={infoGroupStyle}>
          <span>
            Unit PIN Code Issued{pin.issuedAt ? ` on ${formatDateOnly(pin.issuedAt)}` : ''}
          </span>
          <span style={{ color: ICON_COLORS.grey }}>|</span>
          <span style={{ color: ICON_COLORS.primary }}>Email status:</span>
          {pin.emailStatus === 'failed' ? (
            <>
              <XCircle size={18} color={ICON_COLORS.red} />
              <span style={{ color: ICON_COLORS.red, fontWeight: 500 }}>Failed</span>
            </>
          ) : pin.emailStatus === 'sent' ? (
            <>
              <CheckCircle size={18} color={ICON_COLORS.green} />
              <span style={{ color: ICON_COLORS.green, fontWeight: 500 }}>Sent</span>
            </>
          ) : (
            <span style={{ color: ICON_COLORS.grey, fontWeight: 500 }}>Pending</span>
          )}
        </div>
        <div style={{ ...infoGroupStyle, flexWrap: 'nowrap' }}>
          {canEdit && (pin.emailStatus === 'failed' || pin.emailStatus === 'pending') && (
            <button
              type="button"
              className={`button button-primary ${isResending ? 'is-loading' : ''}`}
              disabled={isResending}
              onClick={() => onResend()}
            >
              Resend an email
            </button>
          )}
          {canEdit && (
            <>
              <button
                type="button"
                className={`button button-primary ${isRevoking ? 'is-loading' : ''}`}
                disabled={isRevoking}
                onClick={() => setShowRevokeConfirm(true)}
              >
                Revoke Unit PIN Code Access
              </button>
              <Tooltip triggerIcon={Info} placement="bottom-end">
                {PIN_TOOLTIP}
              </Tooltip>
            </>
          )}
        </div>
      </div>
      {!pin.hasIglooAccessId && (
        <div style={warningStyle}>
          <WarningCircle size={18} color={ICON_COLORS.red} />
          <span style={{ color: ICON_COLORS.red }}>
            Cannot revoke this PIN from ElevateOS — it is not linked to Iglooworks. Contact support
            to remove it.
          </span>
        </div>
      )}
      <ConfirmPopup
        title="Revoke PIN access?"
        body={`Are you sure you want to revoke PIN access for ${
          residentName || 'this resident'
        }? This will remove the PIN record from ElevateOS and the resident will no longer receive this PIN through ElevateOS. But the PIN remains active on the physical lock until the lock is reset.`}
        cancelText="Cancel"
        confirmText="Revoke PIN"
        isOpen={showRevokeConfirm}
        isLoading={isRevoking}
        cancelCallback={() => setShowRevokeConfirm(false)}
        confirmCallback={async () => {
          try {
            await onRevoke()
          } finally {
            setShowRevokeConfirm(false)
          }
        }}
      />
    </div>
  )
}

IglooPinBlock.propTypes = {
  pin: PropTypes.shape({
    issuedAt: PropTypes.instanceOf(Date),
    emailStatus: PropTypes.oneOf(['sent', 'failed', 'pending', 'none']),
    status: PropTypes.oneOf(['active', 'revoked_pending_sync', 'revoked', 'none']).isRequired,
    revokedAt: PropTypes.instanceOf(Date),
    hasIglooAccessId: PropTypes.bool,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  residentName: PropTypes.string,
  onIssue: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  isIssuing: PropTypes.bool,
  isRevoking: PropTypes.bool,
  isResending: PropTypes.bool,
}

IglooPinBlock.defaultProps = {
  residentName: '',
  isIssuing: false,
  isRevoking: false,
  isResending: false,
}

export default IglooPinBlock
